import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { FormError } from '../shared/FormError';
import { Spinner } from '../../ui/Spinner';
import { useSubmit } from '../../hooks/useSubmit';
import { demoNewsletterLink, newRequestId, subscribeNewsletter } from '../../utils/api';
import { legalLinks } from '../../data/legal';
import { founder } from '../../data/content';
import { newsletterConsent } from '../../data/consent';
import { ease } from '../../core/format/motion';
import { track } from '../../utils/analytics';

/**
 * Nieuwsbrief bovenaan de footer (op klei): één veld, double opt-in, en een duidelijke
 * bevestiging. Alle tekst in shell (5,6:1 op klei); het accent in blush alleen groot.
 */
export function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const signup = useSubmit(subscribeNewsletter);
  const reduce = useReducedMotion();
  return <div className="grid gap-10 py-16 lg:grid-cols-12 lg:items-end lg:gap-16 lg:py-24" data-id="element-7879" data-mp="3bre3zbuzocee">
      <div className="lg:col-span-6" data-id="element-7880" data-mp="3giw8g9w80v44">
        <h2 id="newsletter-heading" className="font-display text-[2.4rem] font-light leading-[1.05] tracking-[-0.015em] text-shell sm:text-[3rem]" data-id="element-7881" data-mp="3gjxh67prl5io">
          De vijf fouten die ik
          <span className="italic text-blush" data-id="element-7882" data-mp="37r3zy4qrbw26"> het vaakst zie.</span>
        </h2>
        <p className="mt-5 max-w-md text-[16px] leading-relaxed text-shell" data-id="element-7883" data-mp="3vf3nkvsrv6cj">
          Meld je aan en je krijgt ze meteen in je inbox. Plus één keer per maand wat {founder.firstName} in de salon opviel. Geen aanbiedingenspam.
        </p>
      </div>
      {/* De bevestiging heeft zelf role="status"; geen extra live-regio nodig. */}
      <div className="lg:col-span-6" data-id="element-7884" data-mp="3qez6p4nyc9ls">
        <AnimatePresence mode="wait" initial={false} data-id="element-7885" data-mp="2a2ado2dum7tv">
          {signup.succeeded ? <motion.p key="done" initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease
        }} role="status" className="flex items-center gap-3 text-[16px] text-shell" data-id="element-7886" data-mp="2a4n659h8g0l6">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-shell text-clay" data-id="element-7887" data-mp="1m6fef12junih">
                <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-7888" data-mp="1h6t34x6a7hcb" />
              </span>
              <span data-id="element-7889" data-mp="1c9u7ubavt4cj">
                Bijna klaar: bevestig je aanmelding via de mail naar {email.trim()}.
                {/* DEMO: met een backend is demoNewsletterLink null en verdwijnt deze knop vanzelf. */}
                {demoNewsletterLink && <Link to={demoNewsletterLink} className="mt-2 block text-[14px] text-shell underline underline-offset-4" data-id="element-7890" data-mp="y8zzttrqx5ee">
                    Open de bevestigingslink (demo)
                  </Link>}
              </span>
            </motion.p> : <motion.form key="form" exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease
        }} aria-labelledby="newsletter-heading" aria-busy={signup.submitting} onSubmit={e => {
          e.preventDefault();
          if (signup.submitting) return;
          void signup.submit({
            requestId: newRequestId(),
            email: email.trim(),
            source: 'footer',
            consentVersion: newsletterConsent.version
          }).then(ok => {
            if (ok) track('nieuwsbrief-aangemeld', {
              bron: 'footer'
            });
          });
        }} data-id="element-7891" data-mp="2snsw7v0ge07e">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end" data-id="element-7892" data-mp="2r4aob7tek8ww">
                <label htmlFor="footer-email" className="sr-only" data-id="element-7893" data-mp="2v7peuseyw7ol">
                  E-mailadres
                </label>
                <input id="footer-email" type="email" required autoComplete="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jouw@email.nl" aria-invalid={signup.error ? true : undefined} aria-describedby={signup.error ? 'footer-email-error footer-email-hint' : 'footer-email-hint'} className="w-full border-b border-shell/50 bg-transparent px-1 py-3 text-[17px] text-shell placeholder:text-shell/80 transition-colors duration-150 ease-soft focus:border-shell focus:outline-none" data-id="element-7894" data-mp="6f3g8lczmjqd" />
                <button type="submit" disabled={signup.submitting} className="group flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-shell px-7 text-[15px] text-ink transition-[background-color,transform] duration-150 ease-soft hover:bg-blush active:scale-[0.98] disabled:cursor-wait disabled:opacity-80" data-id="element-7895" data-mp="11oqiu8ftenbl">
                  {signup.submitting ? <Spinner data-id="element-7896" data-mp="1pd6mvbsvf1a9" /> : null}
                  Stuur me de vijf fouten
                  {!signup.submitting && <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7897" data-mp="1uaoo0g0ncc7p" />}
                </button>
              </div>
              <p id="footer-email-hint" className="mt-4 text-[12px] leading-relaxed text-shell" data-id="element-7898" data-mp="1lcczcjjcbohw">
                {newsletterConsent.text} Lees ons{' '}
                <Link to={legalLinks.privacy} className="underline underline-offset-2" data-id="element-7899" data-mp="1bd483yfv0nz8">
                  privacybeleid
                </Link>
                .
              </p>
              <FormError id="footer-email-error" message={signup.error} className="mt-4" data-id="element-7900" data-mp="11m13wnrmvsvq" />
            </motion.form>}
        </AnimatePresence>
      </div>
    </div>;
}